import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useState } from "react";
import { createTable, updateTable } from "../../../../../../api/application/table";
import type { APITable } from "../../../../../../api/infrastructure/table.formatter";
import {
  type TableColumnTempInput,
  transformToCreateTableInput,
} from "../../../../../../api/infrastructure/table.transformer";
import Icon from "../../../../../../components/icons/Icon";
import { transformToValidTableColumnName } from "../../../../../shared/add-table-column";
import makeConfirmDialog from "../../../../utils/ConfirmDialog/ConfirmDialog";
import AddTableColumn from "./AddTableField/AddTableColumn";
import TableColumnDataTable from "./TableColumnDataTable";

import { nbValOrUndef } from "@paroi/data-formatters-lib";
import { useParams } from "react-router-dom";
import { syncOpenModalDialog } from "../../../../../shared/global-tools/dialog-system";
import { showToast } from "../../../../../shared/global-tools/toast-system";
import "./TableCard.scss";

interface TableCardProps {
  referenceId: number;
  tableNumber: number;
  initCollapse?: boolean;
  currentTable: APITable;
  tables: APITable[];
  onRemoveTable(removeTableId: string): Promise<void>;
  refreshTableList(): void;
}

export default function TableCard({
  referenceId,
  tableNumber,
  initCollapse,
  currentTable,
  tables,
  onRemoveTable,
  refreshTableList,
}: TableCardProps) {
  const { databaseId } = useParams();
  const formatedReferenceToEditId = nbValOrUndef(databaseId);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [collapse, setCollapse] = useState(initCollapse ?? true);
  const wrapAsync = useWrapAsync();
  const [loading, setLoading] = useState(false);
  const [deletingTable, setDeletingTable] = useState(false);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [tableColumnInputs, setTableColumnInputs] = useState<TableColumnTempInput[]>([]);

  const deleteTable = () => {
    syncOpenModalDialog(makeConfirmDialog, {
      onConfirm: () => performTableDeletion(currentTable.id),
      warningMessage: (
        <>
          Voulez-vous supprimer la table <b>{currentTable.name}</b> ?
        </>
      ),
    });
  };

  const performTableDeletion = wrapAsync(async (tableId: string) => {
    try {
      setDeletingTable(true);
      await onRemoveTable(tableId);
      refreshTableList();
      showToast("Table supprimée avec succès", { severity: "success" });
    } catch (error) {
      console.error("Error deleting table:", error);
      showToast("Echec suppression de la table", { severity: "error" });
    } finally {
      setDeletingTable(false);
    }
  });

  const performTableCreation = wrapAsync(async () => {
    if (label === "") {
      showToast("Le nom de la table est requis", { severity: "warn" });
      return;
    }
    if (tableColumnInputs.length === 0) {
      showToast("Au moins une colonne est requise", { severity: "warn" });
      return;
    }
    const validTableName = transformToValidTableColumnName(label);
    const isNameTaken = tables.some((t) => t.name === validTableName && t.id !== currentTable.id);
    if (isNameTaken) {
      showToast("Le nom de la table est déjà utilisé", {
        severity: "warn",
      });
      return;
    }
    try {
      setLoading(true);
      if (currentTable.id.includes("temp_")) {
        await createTable(
          referenceId,
          transformToCreateTableInput({
            label,
            name: transformToValidTableColumnName(label),
            description,
            columns: tableColumnInputs,
          }),
        );
      } else {
        const tableIdToUpdate = Number(currentTable.id);
        if (Number.isNaN(tableIdToUpdate)) {
          showToast("table id is invalid", { severity: "warn" });
          return;
        }
        await updateTable(
          referenceId,
          tableIdToUpdate,
          transformToCreateTableInput({
            label,
            name: transformToValidTableColumnName(label),
            description,
            columns: tableColumnInputs,
          }),
        );
      }

      refreshTableList();
      showToast("Table enregistrée avec succès", { severity: "success" });
    } catch (error) {
      console.error(error);
      showToast("Echec enregistrement de la table", { severity: "error" });
    } finally {
      setLoading(false);
    }
  });

  const onLabelChange = (val: string) => {
    const oldErrors = new Map(errors);
    if (val === "") {
      oldErrors.set("name", "Ce champ est requis");
      setErrors(oldErrors);
    } else {
      oldErrors.delete("name");
      setErrors(oldErrors);
    }
    setLabel(val);
  };
  const onDescriptionChange = (val: string) => {
    setDescription(val);
  };

  useEffect(() => {
    setLabel(currentTable.name);
    setDescription(currentTable.description ?? "");
    setTableColumnInputs(
      currentTable.columns.map((col) => ({ ...col, nullable: false, unique: col.unique })),
    );
  }, [currentTable]);

  return (
    <div className="TableCard">
      <div className="TableCard-header">
        <div className="TableCard-headerLeft">
          <h3 className="TableCard-headerTitle">Table {tableNumber}</h3>
          <button type="button" className="IconBtn" onClick={deleteTable} disabled={deletingTable}>
            <Icon name="trash" color="#EF0404" />
          </button>
        </div>

        <button type="button" className="IconBtn" onClick={() => setCollapse(!collapse)}>
          <Icon name="chevron-down" style={{ rotate: collapse ? undefined : "180deg" }} />
        </button>
      </div>

      {!collapse && (
        <div className="TableCard-body">
          <div className="TableCard-form">
            <label className="Field">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Nom de la table <span className="Field-asterisk">*</span>
              </span>
              <InputText
                className="Field-input w-full"
                placeholder="Nom de la table *"
                value={label}
                onChange={(e) => onLabelChange(e.target.value)}
                invalid={!!errors.get("name")}
                disabled={!!formatedReferenceToEditId}
              />
              <small className="Field-errorMessage">{errors.get("name")}</small>
            </label>

            <label className="Field">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Description de la table
              </span>
              <InputTextarea
                rows={2}
                className="Field-textarea w-full"
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
              />
            </label>
          </div>

          <AddTableColumn
            currentTable={currentTable}
            tables={tables}
            tableColumnInputs={tableColumnInputs}
            onChange={(val) => {
              setTableColumnInputs([...tableColumnInputs, val]);
            }}
          />

          <TableColumnDataTable
            currentTable={currentTable}
            tables={tables}
            tableColumnInputs={tableColumnInputs}
            onChange={(updatedFields) => {
              setTableColumnInputs(updatedFields);
            }}
          />

          <div style={{ display: "flex", justifyContent: "center", marginTop: 10, width: "100%" }}>
            <Button
              type="button"
              label="Valider"
              className="TableCard-btn Btn primary"
              onClick={performTableCreation}
              disabled={loading || label === "" || !tableColumnInputs.length}
            />
          </div>
        </div>
      )}
    </div>
  );
}
